import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { LoadingContext, M_StateActions } from "src/M_Components";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import "../relation-management.css";

import DynamicSelectBox from "src/components/Forms/DynamicSelectBox";
import SelectableTileGrid from "src/components/SelectableTileGrid/SelectableTileGrid";
import RoutePreviewTable from "src/components/routePreview/routePreviewTable";

/* =====================================================================================================================
 * Helpers
 * =================================================================================================================== */

/**
 * Key used to persist and restore the lanes grid UI state per tab.
 *
 * The stored value is an opaque object provided by SelectableTileGrid.
 * This value typically contains sorting, filtering, pagination, and search configuration
 * that should be restored when switching back to the tab.
 */
const K_GRID_LANES = "RLM.grid.lanes";

/**
 * Converts an input into a finite number.
 *
 * Purpose:
 * - Normalizes identifiers coming from UI components that may emit strings or numbers.
 * - Prevents accidental NaN usage when building API requests or performing lookups.
 *
 * Behavior:
 * - Returns null when the value is null/undefined, an empty string, or not a finite number.
 *
 * @param {any} v Input value that should represent a numeric identifier
 * @returns {number|null} A finite number when valid; otherwise null
 */
const toNumberOrNull = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/* =====================================================================================================================
 * RouteLaneManagement
 * =================================================================================================================== */

/**
 * RouteLaneManagement provides a relationship management screen that links/unlinks lanes to a route.
 *
 * Layout:
 * - Left panel:
 *   - Route selector (single selection)
 *   - SelectableTileGrid showing lanes with the ability to toggle selection
 * - Right panel:
 *   - Map preview showing all linked lanes for the selected route
 *
 * Data flow:
 * - Route selection drives a fetch of RouteLaneRels/Route/{routeId}/Lanes.
 * - Grid selection state is derived from the fetched relations.
 * - Toggling a lane performs a link/unlink API call, then updates local state for immediate feedback.
 *
 * Visual behavior:
 * - Global loader is used to cover page-level operations and to smooth map rendering updates.
 *
 * @param {{ onBack: function, toastMessages: function }} props Component props
 * @returns {JSX.Element} Relationship management screen
 */
export default function RouteLaneManagement({ onBack, toastMessages }) {
  /**
   * Translation function used for labels and user messages.
   */
  const { t } = useTranslation();

  /**
   * Global loading context used to display and hide the page loader.
   */
  const ctx = useContext(LoadingContext);

  /**
   * Redux dispatch used to persist tab-scoped session values.
   */
  const dispatch = useDispatch();

  /**
   * Global state store used as a tab-scoped snapshot store.
   * Each tab id stores a list of { key, value } entries.
   */
  const M_State = useSelector((state) => state.StateKeyValue);

  /**
   * Current selected tab identifier.
   *
   * Purpose:
   * - Provides the active tab namespace for per-tab persistence.
   * - Drives rehydration when the user switches between tabs.
   */
  const selectedTab = useSelector(
    (state) => state.StateKeyValue.find((o) => o.key === "selectedTab")?.value
  );

  /**
   * Stores the linked lanes for the currently selected route.
   *
   * Purpose:
   * - Drives which lanes appear selected in the grid.
   * - Feeds RoutePreviewTable when used in controlled mode.
   *
   * Shape:
   * [{ laneId:number, isActive:boolean }]
   */
  const [linkedLaneRels, setLinkedLaneRels] = useState([]);

  /**
   * List of linked lane identifiers derived from linkedLaneRels.
   *
   * Purpose:
   * - Provides SelectableTileGrid with the selectedIds array.
   * - Allows quick membership checks when toggling.
   */
  const linkedLanes = useMemo(
    () => linkedLaneRels.map((x) => x.laneId).filter(Number.isFinite),
    [linkedLaneRels]
  );

  /**
   * Tab-scoped identifiers and selection state.
   *
   * id:
   * - Tracks the current tab identifier and is used as a namespace key in M_State.
   * - Maintains a stable fallback namespace when selectedTab is temporarily undefined.
   *
   * selectedRouteId:
   * - Stores the current route selection for this tab.
   * - Is persisted so selection is restored when returning to the tab.
   */
  const [id, setId] = useState(() => selectedTab);
  const [selectedRouteId, setSelectedRouteId] = useState(null);

  /**
   * Flag that indicates per-tab state restoration has been attempted.
   *
   * Purpose:
   * - Ensures default selection logic runs only after a session restore attempt.
   * - Prevents default selection from overriding a saved tab selection.
   */
  const [hasRestoredFromSession, setHasRestoredFromSession] = useState(false);

  /**
   * Key name used to store and restore the selected route id within the tab session.
   */
  const sessionKeySelected = "routeLane_selectedRouteId";

  /**
   * Base URL for API requests as provided by the loading context.
   */
  const baseUrl = ctx.DEFAULTURL;

  /**
   * Service used to query available routes.
   *
   * Purpose:
   * - Supports initial default route selection when no selection exists.
   *
   * Memoization prevents recreating the service on every render.
   */
  const routesBuffer = useMemo(
    () => new BufferService("Routes", baseUrl),
    [baseUrl]
  );

  /**
   * Upserts a key/value entry into the tab-scoped session array.
   *
   * Purpose:
   * - Stores UI state (selected route id, grid state) under the current tab id.
   * - Avoids replacing the entire session array by removing only the matching key.
   *
   * Behavior:
   * - Reads the current session array for tabId from M_State.
   * - Removes the entry with the same key.
   * - Appends the new entry and writes the updated array back to M_State.
   *
   * @param {string|number} tabId Tab identifier used as session namespace
   * @param {{key:string,value:any}} kv Entry to store in the session
   */
  const upsertSession = useCallback(
    (tabId, kv) => {
      const current = M_State.find((o) => o.key === tabId)?.value || [];
      const filtered = current.filter((e) => e.key !== kv.key);
      dispatch(
        M_StateActions.addM_State({ key: tabId, value: [...filtered, kv] })
      );
    },
    [M_State, dispatch]
  );

  /**
   * Reads a stored value from a session array that uses the shape [{key,value}, ...].
   *
   * @param {Array<{key:string,value:any}>} arr Session array
   * @param {string} k Key to read
   * @returns {any} Stored value or undefined when not found
   */
  const pick = (arr, k) => (arr || []).find((x) => x.key === k)?.value;

  /**
   * Rehydrates per-tab state when the active tab changes or when the tab session is updated.
   *
   * Responsibilities:
   * - Resolve the active tab id using selectedTab with a fallback to local id.
   * - Keep local id aligned with the current tab id.
   * - Restore the saved route selection for that tab when present.
   * - Mark restoration as attempted so default selection logic can run safely.
   *
   * Notes:
   * - This effect depends on M_State, selectedTab, and id to ensure restoration runs when either:
   *   - The user switches tabs, or
   *   - The session snapshot for a tab becomes available/changes.
   */
  useEffect(() => {
    const currentTabId = selectedTab || id;
    if (!currentTabId) return;

    // Keep the local namespace aligned with the active tab.
    if (currentTabId !== id) setId(currentTabId);

    // Read the session array for the active tab namespace.
    const session = M_State.find((obj) => obj.key === currentTabId)?.value;

    // Restore the saved route selection if available.
    if (session) {
      const savedRouteId = pick(session, sessionKeySelected);
      if (savedRouteId !== undefined) {
        setSelectedRouteId(savedRouteId);
        setHasRestoredFromSession(true);
        return;
      }
    }

    // Mark restoration as attempted even when no saved value exists.
    setHasRestoredFromSession(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State, selectedTab, id]);

  /**
   * Persists the current route selection under the current tab session.
   *
   * Purpose:
   * - Allows route selection to survive tab switching.
   * - Ensures the same route is restored when returning to the tab.
   *
   * Behavior:
   * - Uses the active tab id when available, otherwise falls back to the local id.
   * - Avoids writes before a valid tab id exists.
   */
  useEffect(() => {
    const currentTabId = id || selectedTab;
    if (!currentTabId) return;

    upsertSession(currentTabId, {
      key: sessionKeySelected,
      value: selectedRouteId,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selectedTab, selectedRouteId]);

  /**
   * Default selection strategy for Route.
   *
   * Behavior:
   * - Waits until a session restore attempt is complete.
   * - When no route selection exists, requests the first active route ordered by description.
   * - Stores its Id into selectedRouteId when found.
   *
   * Failure handling:
   * - Errors are ignored to keep the screen functional even when no default can be resolved.
   */
  useEffect(() => {
    // Ensure session restore gets priority over default selection.
    if (!hasRestoredFromSession) return;

    // When a route selection already exists (restored or user-selected), do nothing.
    if (selectedRouteId != null) return;

    ctx.setIsLoad(true);

    (async () => {
      try {
        // Order routes by description and exclude inactive ones.
        const order = encodeURIComponent("Description asc");
        const filter = encodeURIComponent("Inactive eq false");

        // Query a single page with a single item to obtain a default candidate.
        const res = await routesBuffer.filter(
          `?Page=1&PageSize=1&OrderBy=${order}&Filter=${filter}`
        );
        if (isFailurePayload(res)) return;

        // Normalize response shape to a list of route items.
        const items = Array.isArray(res?.Items)
          ? res.Items
          : Array.isArray(res)
            ? res
            : [];

        // Select the first route when available.
        const first = items?.[0];
        if (first?.Id != null) setSelectedRouteId(first.Id);
      } catch {
        // The default selection path is intentionally tolerant to failures.
      } finally {
        ctx.setIsLoad(false);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRestoredFromSession, routesBuffer, selectedRouteId]);

  /**
   * Normalized route id used by downstream effects and rendering.
   *
   * Purpose:
   * - Avoids repeated nullish checks across the component.
   */
  const routeId = selectedRouteId ?? null;

  /**
   * Timeout reference used to smooth loading indicator behavior after map updates.
   */
  const mapRenderDelayRef = useRef(null);

  /**
   * Ends the global loader after a short delay to reduce flicker while map layers update.
   *
   * Purpose:
   * - Lane toggles trigger immediate UI updates and also trigger map updates.
   * - The map component may update asynchronously; a short delay reduces rapid loader toggling.
   *
   * Behavior:
   * - Clears any pending timeout.
   * - Schedules a delayed ctx.setIsLoad(false) after the next animation frame.
   */
  const endLoadingAfterMap = useCallback(() => {
    if (mapRenderDelayRef.current) clearTimeout(mapRenderDelayRef.current);

    requestAnimationFrame(() => {
      mapRenderDelayRef.current = setTimeout(() => {
        ctx.setIsLoad(false);
      }, 220);
    });
  }, [ctx]);

  /**
   * Cleanup effect that clears pending timeouts during unmount.
   *
   * Purpose:
   * - Prevents state updates after unmount.
   * - Avoids keeping timers alive longer than necessary.
   */
  useEffect(() => {
    return () => {
      if (mapRenderDelayRef.current) clearTimeout(mapRenderDelayRef.current);
    };
  }, []);

  /**
   * Toggles a link/unlink operation for a given lane id.
   *
   * Purpose:
   * - Adds a lane relation when the lane is not currently linked to the selected route.
   * - Removes a lane relation when the lane is currently linked to the selected route.
   *
   * Behavior:
   * - Validates that a route is selected and the lane id is a finite number.
   * - Selects the correct API request based on current linkage status.
   * - Updates linkedLaneRels optimistically after a successful API response.
   * - Displays toast feedback for success and failure.
   * - Uses endLoadingAfterMap to stop the loader after the map has had time to update.
   *
   * @param {number|string} laneIdRaw Lane identifier provided by the grid item
   */
  const toggleLane = (laneIdRaw) => {
    // Linking requires a selected route.
    if (!routeId) return;

    // Normalize and validate lane identifier.
    const laneId = toNumberOrNull(laneIdRaw);
    if (!laneId) return;

    // Start loader for the link/unlink request.
    ctx.setIsLoad(true);

    // Determine whether the lane is currently linked based on selected ids.
    const isLinked = linkedLanes.includes(laneId);

    // Create endpoint services for unlinking and linking operations.
    // Unlink uses a route-scoped endpoint and deletes a lane relation.
    const unlinkBuffer = new BufferService(`RouteLaneRels/${routeId}`, baseUrl);

    // Link uses a create endpoint and posts a new route-lane relation payload.
    const linkBuffer = new BufferService("RouteLaneRels/create", baseUrl);

    // Select the request based on the lane linkage status.
    const request = isLinked
      ? unlinkBuffer.delete(laneId)
      : linkBuffer.post({ routeId, laneId, isActive: true });

    request
      .then((res) => {
        // Convert API failures into localized user feedback.
        if (isFailurePayload(res)) {
          toastMessages("error", "", apiMessage(res, t("ErrorSaving")));
          return;
        }

        // Update local relation list so the grid selection and map update immediately.
        setLinkedLaneRels((prev) => {
          if (isLinked) return prev.filter((x) => x.laneId !== laneId);
          return [...prev, { laneId, isActive: true }];
        });

        // Provide a success message reflecting the action performed.
        toastMessages("success", "", isLinked ? t("LaneRemoved") : t("LaneAdded"));
      })
      .catch((err) => toastMessages("error", "", apiMessage(err, t("ErrorSaving"))))
      .finally(() => {
        // Stop loading after a short delay to allow map layers to settle.
        endLoadingAfterMap();
      });
  };

  /**
   * Initial state passed into SelectableTileGrid for the lanes grid.
   *
   * Purpose:
   * - Restores user grid configuration such as sorting, filtering, paging, and search.
   *
   * Notes:
   * - The stored value is treated as opaque and forwarded back to the grid.
   */
  const gridInitialState = useMemo(() => {
    const currentTabId = id || selectedTab;
    if (!currentTabId) return undefined;

    const session = M_State.find((o) => o.key === currentTabId)?.value || [];
    return pick(session, K_GRID_LANES) || undefined;
  }, [M_State, id, selectedTab]);

  /**
   * Loads route-lane relations for the selected route and stores them in linkedLaneRels.
   *
   * Purpose:
   * - Keeps the lanes grid selection aligned with backend relations.
   * - Provides RoutePreviewTable with relationship data when it is used in controlled mode.
   *
   * Pagination:
   * - Uses a page loop with a defensive page size.
   * - Continues until no more items are returned or TotalPages is reached.
   *
   * Aggregation logic:
   * - Collects relations into a Map keyed by lane id.
   * - When the same lane id is encountered multiple times, the stored isActive becomes true
   *   if any relation indicates active.
   *
   * Filtering:
   * - Applies "Inactive eq false" to exclude soft-deleted relations at the API level.
   */
  useEffect(() => {
    // Without a selected route, there are no relations to display.
    if (!routeId) {
      setLinkedLaneRels([]);
      return;
    }

    let cancelled = false;

    // Start loader while relations are being fetched.
    ctx.setIsLoad(true);

    (async () => {
      try {
        const pageSize = 50;
        let page = 1;

        // Accumulates the strongest active value per lane id.
        const acc = new Map();

        // Service that returns relations for the route in a paged format.
        const routeLanesBuffer = new BufferService(
          `RouteLaneRels/Route/${routeId}/Lanes`,
          baseUrl
        );

        // Excludes soft-deleted relations (Inactive = true).
        const filterInactiveFalse = encodeURIComponent("Inactive eq false");

        while (true) {
          // Fetch one page of relations.
          const res = await routeLanesBuffer.filter(
            `?Page=${page}&PageSize=${pageSize}&Filter=${filterInactiveFalse}`
          );

          // Stop processing when the effect has been cancelled.
          if (cancelled) return;

          // Stop on API failures, keeping the last stable state until error handling completes.
          if (isFailurePayload(res)) break;

          // Normalize response to an array of items.
          const items = Array.isArray(res?.Items)
            ? res.Items
            : Array.isArray(res)
              ? res
              : [];

          // Break the loop when no more items exist.
          if (!items?.length) break;

          // Merge page items into the accumulator.
          for (const it of items) {
            // Normalize lane id from common payload shapes.
            const laneId = toNumberOrNull(it?.LaneId ?? it?.laneId ?? it?.Id ?? it?.id);
            if (!laneId) continue;

            // Normalize active value, tolerating boolean and string representations.
            const isActiveRaw = it?.IsActive ?? it?.isActive;
            const boolActive =
              isActiveRaw === true || String(isActiveRaw).toLowerCase() === "true";

            // Store the strongest "active" value per lane id.
            if (!acc.has(laneId)) acc.set(laneId, boolActive);
            else if (boolActive) acc.set(laneId, true);
          }

          // Determine pagination end condition.
          const totalPages = Number(
            res?.TotalPages ?? (items.length < pageSize ? page : page + 1)
          );
          if (page >= totalPages) break;

          page += 1;
        }

        // Prevent state updates when cancelled.
        if (cancelled) return;

        // Convert the accumulator map into the canonical linkedLaneRels shape.
        setLinkedLaneRels(
          Array.from(acc.entries()).map(([laneId, isActive]) => ({
            laneId,
            isActive: !!isActive,
          }))
        );
      } catch (err) {
        // Avoid state updates when cancelled.
        if (!cancelled) {
          // Display a user-friendly error and reset local relations to an empty state.
          toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
          setLinkedLaneRels([]);
        }
      } finally {
        // Stop loader when still mounted and active.
        if (!cancelled) ctx.setIsLoad(false);
      }
    })();

    // Cancellation prevents stale async work from updating state after routeId changes.
    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, baseUrl]);

  return (
    <div className="firstDivContainer">
      <div className="rlm-layout">
        {/* Left panel: route selection and lanes grid */}
        <div className="rlm-panel rlm-left">
          <div className="rel-section">
            <span className="rel-title">{t("Route")}</span>

            {/* Route selector creates the primary scope for all link/unlink operations. */}
            <DynamicSelectBox
              label={t("Route")}
              tableName="Routes"
              groupBy="Description"
              valueId={selectedRouteId}
              onChange={(opt) => setSelectedRouteId(opt?.value ?? null)}
              required
              pageSize={20}
            />
          </div>

          {/* The lanes grid is shown only when a route is selected. */}
          {routeId && (
            <div className="rel-grid-section">
              <div className="rel-grid-box-500-700">
                <SelectableTileGrid
                  title={t("Lanes")}
                  endpoint="Lanes"
                  idField="Id"
                  displayField="Name"
                  pageSize={20}
                  queryable
                  orderable
                  staticFilter="Inactive eq false"
                  selectedIds={linkedLanes}
                  onItemToggle={(item) => {
                    // Normalize lane id from common DTO shapes.
                    const laneId = item?.Id ?? item?.id;
                    if (laneId != null) toggleLane(laneId);
                  }}
                  // Scope identifier isolates persisted grid state across tabs and screens.
                  stateScopeId={`${id || selectedTab || "unknown"}::RLM::lanes`}
                  initialState={gridInitialState}
                  onStateChange={(st) => {
                    // Persist grid state per tab so it can be restored on tab revisit.
                    const currentTabId = id || selectedTab;
                    if (!currentTabId) return;
                    upsertSession(currentTabId, { key: K_GRID_LANES, value: st });
                  }}
                  align="vertical"
                />
              </div>
            </div>
          )}
        </div>

        {/* Right panel: map visualization */}
        <div className="rlm-panel rlm-map">
          <div className="rlm-map-stickyWrap">
            <div className="rlm-map-card">
              <div className="rlm-map-inner">
                {/* Map preview is rendered in controlled mode by providing linkedLaneRelsOverride. */}
                <RoutePreviewTable
                  routeId={routeId}
                  linkedLaneRelsOverride={linkedLaneRels}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Prop type validation for RouteLaneManagement.
 *
 * onBack:
 * - Provided by the parent navigation flow.
 * - This component does not invoke onBack directly in this file, but the prop is required
 *   to keep the contract consistent with the screen container.
 *
 * toastMessages:
 * - Displays user feedback for success and error paths.
 */
RouteLaneManagement.propTypes = {
  onBack: PropTypes.func.isRequired,
  toastMessages: PropTypes.func.isRequired,
};
